import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ArrowBendDoubleUpRight } from "@phosphor-icons/react/ArrowBendDoubleUpRight";
import { ArrowsClockwise } from "@phosphor-icons/react/ArrowsClockwise";
import { Briefcase } from "@phosphor-icons/react/Briefcase";
import { Buildings } from "@phosphor-icons/react/Buildings";
import { CloudArrowUp } from "@phosphor-icons/react/CloudArrowUp";
import { Database } from "@phosphor-icons/react/Database";
import { FilePlus } from "@phosphor-icons/react/FilePlus";
import { Flask } from "@phosphor-icons/react/Flask";
import { Globe } from "@phosphor-icons/react/Globe";
import { Lightning } from "@phosphor-icons/react/Lightning";
import { Handshake } from "@phosphor-icons/react/Handshake";
import { Layout } from "@phosphor-icons/react/Layout";
import { Link } from "@phosphor-icons/react/Link";
import { LinkSimple } from "@phosphor-icons/react/LinkSimple";
import { ListMagnifyingGlass } from "@phosphor-icons/react/ListMagnifyingGlass";
import { MapTrifold } from "@phosphor-icons/react/MapTrifold";
import { PaintBrush } from "@phosphor-icons/react/PaintBrush";
import { PersonSimpleRun } from "@phosphor-icons/react/PersonSimpleRun";
import { Quotes } from "@phosphor-icons/react/Quotes";
import { StackSimple } from "@phosphor-icons/react/StackSimple";
import { Tag } from "@phosphor-icons/react/Tag";
import { UsersThree } from "@phosphor-icons/react/UsersThree";
import { contactV1Or2 } from "config/paths";
import { mapPalettes } from "mangomap-ui/src/components/map-field";

import type { BenefitsContent } from "~landing/components/home/benefits";
import type { FaqsContent } from "~landing/components/home/faqs";
import type { FeatureHighlightsContent } from "~landing/components/home/feature-highlights";
import type { HeroContent } from "~landing/components/home/hero";
import type { HowItWorksContent } from "~landing/components/home/how-it-works";
import type { PricingTeaserContent } from "~landing/components/home/pricing-teaser";
import type { StartBuildingContent } from "~landing/components/home/start-building";
import type { TestimonialsContent } from "~landing/components/home/testimonials";
import type { UseCasesContent } from "~landing/components/home/use-cases";

// GIS homepage copy, rendered through the shared home section template.

const hero: HeroContent = {
	eyebrow: "Online GIS Mapping",
	icon: <StackSimple />,
	heading: "Build maps worth sharing.",
	subHeading:
		"Turn finished GIS work into an interactive map anyone can open in any browser.",
	ctaButton: "Sign up free",
	keyObjection: "No credit card required",
	socialProof:
		'"Mango delivers all the functionality we need to grow." - Phil Henry, Managing Director, Business Geographics',
	socialProofAvatar: "/app-assets/images/customer-busgeo-icon.png",
	socialProofLabel: "Trusted by GIS teams at",
	image: "/app-assets/images/hero-map.jpg",
	mapField: {
		seed: "mangohero",
		variant: "parcels",
		palette: mapPalettes.slate,
		strokeWidth: 0.5,
		parcelMinSize: 0.5,
		parcelMaxDepth: 10,
		animate: true,
		duration: 5,
		opacity: 0.12,
	},
	customers: [
		{
			customer: "Sensar",
			src: "/app-assets/images/customer-sensar.png",
		},
		{
			customer: "GIS4AG",
			src: "/app-assets/svg/customer-gis4ag.svg",
		},
		{
			customer: "Spectrum",
			src: "/app-assets/images/customer-spectrum-logo.png",
		},
		{
			customer: "Hexagon Geospatial",
			src: "/app-assets/svg/customer-hexagon.svg",
		},
		{
			customer: "Serco Italia",
			src: "/app-assets/images/customer-serco.png",
		},
		{
			customer: "Enerko",
			src: "/app-assets/svg/customer-enerko.svg",
		},
		{
			customer: "Swift Aerial Surveys",
			src: "/app-assets/svg/customer-swift.svg",
		},
		{
			customer: "GBI Experts",
			src: "/app-assets/svg/customer-gbi.svg",
		},
		{
			customer: "Plumas County",
			src: "/app-assets/images/customer-plumas.png",
		},
	],
};

const benefits: BenefitsContent = {
	heading: "Publish finished GIS work as interactive web maps ",
	subHeading: "Share them with anyone. No software required.",
	cards: [
		{
			eyebrow: "Flexibility",
			icon: <Database />,
			title: "Your formats, your schema. Drop it in.",
			description: "No manual reprojection, no conversion step.",
			image: "/app-assets/images/ink-import-file-formats.png",
		},
		{
			eyebrow: "Instant sharing",
			icon: <LinkSimple />,
			title: "Publish once. Everyone always sees current data.",
			description:
				"Share a link. No re-exporting every time something changes.",
			image: "/app-assets/images/ink-share-link-live-audience.png",
		},
		{
			eyebrow: "Simplicity",
			icon: <PersonSimpleRun />,
			title: "No enterprise subscription. No developer.",
			description: "No re-exporting every time the data changes.",
			image: "/app-assets/images/ink-map-clean-simple.png",
		},
		{
			eyebrow: "Collaboration",
			icon: <UsersThree />,
			title: "Control who sees or edits.",
			description: "Clients, colleagues, or the public. Access on your terms.",
			image: "/app-assets/images/ink-access-permissions.png",
		},
	],
};

const useCases: UseCasesContent = {
	heading: "Teams already doing this with Mango",
	subHeading:
		"From county portals to nationwide surveys — real teams, real maps.",
	cards: [
		{
			id: "use-case-local-government",
			eyebrow: "Local government",
			icon: <Buildings />,
			title: "County and city map portals",
			description:
				"Columbus County, North Carolina replaced an aging server-based GIS with a Mango portal that now draws more than 17,000 views a month — with nothing for residents to install.",
			image: "/app-assets/images/articles/columbus-county-map-portal/hero.jpg",
			link: "/case-studies/columbus-county-map-portal",
		},
		{
			id: "use-case-consultants",
			eyebrow: "Consultants & agencies",
			icon: <Briefcase />,
			title: "Whitelabel your own mapping product",
			description:
				"Australian consultancy Business Geographics whitelabelled Mango to turn its proprietary datasets into self-service, subscription mapping products for its clients.",
			image:
				"/app-assets/images/articles/business-geographics-web-mapping/hero.jpg",
			link: "/case-studies/business-geographics-web-mapping",
		},
		{
			id: "use-case-research",
			eyebrow: "Research & public health",
			icon: <Flask />,
			title: "Turn data into a public story",
			description:
				"A University of Manitoba research team used Mango to map Winnipeg's food deserts, turning socioeconomic data into a story the public could explore.",
			image:
				"/app-assets/images/articles/winnipeg-food-atlas/nrd-d6tu_l3chle-unsplash.jpg",
			link: "/case-studies/winnipeg-food-atlas",
		},
		{
			id: "use-case-energy",
			eyebrow: "Energy & field survey",
			icon: <Lightning />,
			title: "One live map for the whole field team",
			description:
				"GIS consultancy DavSeis kept Europe's biggest seismic survey — 300 people working nationwide — running from one always-current map.",
			image:
				"/app-assets/images/articles/europe-biggest-seismic-survey/hero.jpg",
			link: "/case-studies/europe-biggest-seismic-survey",
		},
	],
};

const testimonials: TestimonialsContent = {
	eyebrow: "Success stories",
	icon: <Quotes />,
	reviews: [
		{
			id: "REV-5",
			author: "Dr. Chris Green",
			title: "Snr Epidemiologist, WRHA",
			logo: "/app-assets/images/customer-wrha.svg",
			comment:
				"Love Mango... compared to ArcGIS online, it provides better visual mapping and a straightforward pricing model.",
		},
		{
			id: "REV-4",
			author: "Hope Kallio",
			title: "GIS Specialist at Mostrom & Associates",
			logo: "/app-assets/images/customer-mostrom.svg",
			comment:
				"Mango does what we need it to do, at a fraction of the cost of its competitors.",
		},
		{
			id: "REV-3",
			author: "Emily Selphe",
			title: "Charlevoix County, MI",
			logo: "/app-assets/images/customer-charlevoix.png",
			comment:
				"A great out-of-the-box product—professional results with minimal time and money spent.",
		},
		{
			id: "REV-2",
			author: "Phil Henry",
			title: "Business Geographics & Qikmaps",
			logo: "/app-assets/images/customer-busgeo.png",
			comment:
				"I like the analogy that Mango use: sometimes it's better to use a scalpel than a swiss army knife.",
		},
		{
			id: "REV-1",
			author: "Jake Tanner",
			title: "Lead GIS Developer/Analyst, Burgex, Inc.",
			logo: "/app-assets/images/customer-burgex.svg",
			comment:
				"Great functionality for client visualisation and easy distribution. It's a game-changer.",
		},
	],
};

const howItWorks: HowItWorksContent = {
	steps: [
		{
			id: "import",
			eyebrow: "Import",
			icon: <CloudArrowUp />,
			image: "/app-assets/images/ink-import-to-layers.png",
			imageAlt: "Uploading GIS files into MangoMap",
			title: "Bring your data",
			description:
				"Drop in a Shapefile, GeoJSON, GeoTIFF, KML, or CSV. Your schema comes with it. Nothing to remap.",
		},
		{
			id: "design",
			eyebrow: "Design",
			icon: <MapTrifold />,
			image: "/app-assets/images/ink-style-editor.png",
			imageAlt: "Styling and configuring a map in MangoMap",
			title: "Build your maps",
			description:
				"Style layers, configure popups, set the extent. Your cartography, not a default template.",
		},
		{
			id: "distribute",
			eyebrow: "Distribute",
			icon: <ArrowBendDoubleUpRight />,
			image: "/app-assets/images/ink-share-link-copy.png",
			imageAlt: "Sharing a live map link from MangoMap",
			title: "Share a link",
			description:
				"Share maps or open data with one URL. You decide who can open it.",
		},
		{
			id: "update",
			eyebrow: "Update",
			icon: <ArrowsClockwise />,
			image: "/app-assets/images/ink-map-update-refresh.png",
			imageAlt: "Updated map data reflected in a live MangoMap view",
			title: "Keep it current",
			description: "Change the data. The map updates. The link stays the same.",
		},
		{
			id: "organize",
			eyebrow: "Organize",
			icon: <Layout />,
			image: "/app-assets/images/ink-maps-dashboard-organize.png",
			imageAlt: "Managing projects and access in MangoMap",
			title: "Manage your projects",
			description: "Every map, every client, every project in one place.",
		},
		{
			id: "handoff",
			eyebrow: "Handoff",
			icon: <Handshake />,
			image: "/app-assets/images/ink-map-published-view.png",
			imageAlt: "A published MangoMap portal being viewed in the browser",
			title: "Let clients self-serve",
			description:
				"Share a portal your clients can explore without calling you.",
		},
	],
};

const faqs: FaqsContent = {
	faqContent: {
		eyebrow: "FAQ",
		heading: "Need more answers?",
		subHeading: (
			<Typography color="text.secondary">
				Check our&nbsp;
				<Box
					component="a"
					href="https://help.mangomap.com/hc/en-us/sections/360007615811-Frequently-Asked-Questions"
					sx={{ color: "inherit", textDecoration: "underline" }}>
					complete FAQ
				</Box>
				&nbsp;in the help center, or&nbsp;
				<Box
					component="a"
					href={contactV1Or2}
					sx={{ color: "inherit", textDecoration: "underline" }}>
					contact us
				</Box>
				&nbsp;directly.
			</Typography>
		),
	},
	faqs: [
		{
			id: "FAQ-1",
			question: "Does it work with my existing GIS formats?",
			answer:
				"Yes. Shapefile, TAB, GeoJSON, GeoTIFF, KML, WMS, CSV with coordinates, whatever you're already working with. Import your data, we'll handle the rest.",
		},
		{
			id: "FAQ-2",
			question: "How secure is my data?",
			answer:
				"Your data stays yours. 256-bit encryption, secure hosting, and granular access controls at both the map and data level mean you decide exactly who sees what. Nothing is public until you choose to make it so.",
		},
		{
			id: "FAQ-3",
			question: "Will non-GIS users be able to use the maps?",
			answer:
				"That's the whole point. Send a link to a client, a council member, or a project manager. They'll open it in a browser on their phone without calling you for help.",
		},
		{
			id: "FAQ-4",
			question: "Can I control who sees my maps?",
			answer:
				"You're in charge. Set precise access levels for teams, clients, or the public with a few clicks. Privacy made simple.",
		},
	],
};

const featureHighlights: FeatureHighlightsContent = {
	heading: "Everything between the data and the link.",
	features: [
		{
			eyebrow: "Import",
			title: "Vector Data Support",
			description:
				"Shapefile, MapInfo Tab, KML, CSV, FileGeodatabase, and GeoJSON. Upload what you already have.",
			icon: (
				<FilePlus
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Filter",
			title: "Ask Your Data Anything",
			description:
				"Build powerful filters on any field - without writing a line of SQL.",
			icon: (
				<ListMagnifyingGlass
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Style",
			title: "Advanced Symbology Builder",
			description:
				"Thematic, category, heatmap, and custom graphic styles. Built once, reused across every map.",
			icon: (
				<PaintBrush
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Share",
			title: "Share with Anyone",
			description:
				"Public links for clients, login-required links for internal audiences. You decide who sees what.",
			icon: (
				<Link
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Brand",
			title: "Fully Branded Maps",
			description:
				"Custom domain, logo, and colours, so every map you publish looks like it came from you, not us.",
			icon: (
				<Tag
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Analyze",
			title: "Spatial Analysis",
			description:
				"Proximity, buffer, and intersection. Run the analyses your clients need without opening desktop GIS software.",
			icon: (
				<Globe
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
	],
};

const pricingTeaser: PricingTeaserContent = {
	heading: "The publishing job shouldn't cost enterprise money.",
	subHeading: "Fixed price plans. No surprises.",
	ctaLabel: "See pricing",
};

const startBuilding: StartBuildingContent = {
	heading: "Your next map takes minutes to publish.",
	subHeading:
		"The data is ready. Getting the map out shouldn't take another week.",
	ctaButton: "Start your free trial",
	ctaFooter: "No credit card required.",
	image: "/app-assets/images/cta-map.jpg",
};

export const gisContent = {
	hero,
	benefits,
	useCases,
	testimonials,
	howItWorks,
	faqs,
	featureHighlights,
	pricingTeaser,
	startBuilding,
};
